import { makeAutoObservable, runInAction } from 'mobx'
import { isCancel } from 'axios'
import { debounce } from 'lodash'
import { logger } from 'shared/lib/logger'
import { RequestCancelAxios } from 'shared/api/lib/RequestCancelAxios'

type LoadingStatusType = 'idle' | 'loading' | 'rejected' | 'fulfilled'
type StatusType = {
  isIdle: boolean
  isLoading: boolean
  isFailed: boolean
  isSuccess: boolean
  error: string
}

type ILoadDataProps = {
  status: StatusType
  cancelRequest: RequestCancelAxios
}

type IRequestLoadDataConfig<A, T> = {
  debounceDelay?: number
  preRequestCallback?(arg?: A): void
  requestCallback?(v: ILoadDataProps, arg?: A): Promise<T>
  errorCallback?: (v: unknown, arg?: A) => void
  finallyCallback?: (arg?: A) => void
  finallyEndCallback?: (arg?: A) => void
  loading?: boolean
}

type IRequestLoadDataConfigBase<A, T> = {
  preRequestCallback?(arg?: A): void
  requestCallback?(v: ILoadDataProps, arg?: A): Promise<T>
  errorCallback?: (v: unknown) => void
  finallyCallback?: (arg?: A) => void
  finallyEndCallback?: (arg?: A) => void
}

export class RequestLoadData<A = undefined, T = undefined> {
  private _loadingStatus: LoadingStatusType = 'idle'
  private _error = ''
  private _cancelRequest = new RequestCancelAxios()
  private _debounceDelay = 500
  private _preRequestCallback: IRequestLoadDataConfig<A, T>['preRequestCallback']
  private _requestCallback: IRequestLoadDataConfig<A, T>['requestCallback']
  private _errorCallback: IRequestLoadDataConfig<A, T>['errorCallback']
  private _finallyCallback: IRequestLoadDataConfig<A, T>['finallyCallback']
  private _finallyEndCallback: IRequestLoadDataConfig<A, T>['finallyEndCallback']

  constructor(private _config?: IRequestLoadDataConfig<A, T>) {
    makeAutoObservable(this)

    this._debounceDelay = this._config?.debounceDelay || this._debounceDelay
    this._preRequestCallback = this._config?.preRequestCallback
    this._requestCallback = this._config?.requestCallback
    this._errorCallback = this._config?.errorCallback
    this._finallyCallback = this._config?.finallyCallback
    this._finallyEndCallback = this._config?.finallyEndCallback

    if (typeof this._config?.loading === 'boolean') {
      this.setLoading(this._config.loading)
    }
  }

  loadDataRequest = async <R>(
    {
      preRequestCallback,
      requestCallback,
      errorCallback,
      finallyCallback,
      finallyEndCallback,
    }: IRequestLoadDataConfigBase<A, R>,
    arg?: A
  ) => {
    let isCanceled = false

    try {
      this._error = ''
      this._loadingStatus = 'loading'
      this._cancelRequest.init()
      preRequestCallback?.(arg)

      const result = await requestCallback?.(
        {
          status: this.status,
          cancelRequest: this._cancelRequest,
        },
        arg
      )

      runInAction(() => {
        this._loadingStatus = 'fulfilled'
      })

      return result
    } catch (e) {
      isCanceled = isCancel(e)

      runInAction(() => {
        this._loadingStatus = isCanceled ? 'loading' : 'rejected'

        if (!isCanceled) {
          if (e instanceof Error) {
            this._error = e.message
          }
        }
      })

      if (!isCanceled) {
        errorCallback?.(e)
      }

      logger.error(e)
    } finally {
      if (!isCanceled) {
        finallyCallback?.(arg)
      }

      finallyEndCallback?.(arg)
    }
  }

  blockingLoadDataRequest = <R>(data: IRequestLoadDataConfigBase<A, R>, arg?: A) => {
    if (this._loadingStatus === 'loading') return

    return this.loadDataRequest<R>(data, arg)
  }

  loadData = async (arg?: A): Promise<T | undefined> => {
    return this.loadDataRequest(
      {
        preRequestCallback: this._preRequestCallback,
        requestCallback: this._requestCallback,
        errorCallback: this._errorCallback,
        finallyCallback: this._finallyCallback,
        finallyEndCallback: this._finallyEndCallback,
      },
      arg
    )
  }

  blockingLoadData = (arg?: A) => {
    if (this._loadingStatus === 'loading') return

    return this.loadData(arg)
  }

  debounceLoadData = (arg?: A) => {
    this._cancelRequest.init()

    runInAction(() => {
      this._loadingStatus = 'loading'
    })

    this._debounceLoadData(arg)
  }

  private _debounceLoadData = debounce(this.loadData, this._debounceDelay)

  setError = (error: Error | string) => {
    this._loadingStatus = 'rejected'
    this._error = typeof error === 'string' ? error : error.message
  }

  dispose = () => {
    this._debounceLoadData.cancel?.()
    this._cancelRequest.reset()
    this._loadingStatus = 'idle'
    this._error = ''
  }

  setLoading = (value: boolean) => {
    this._loadingStatus = value ? 'loading' : 'idle'
  }

  get status(): StatusType {
    return {
      isIdle: this._loadingStatus === 'idle',
      isLoading: this._loadingStatus === 'loading',
      isFailed: this._loadingStatus === 'rejected',
      isSuccess: this._loadingStatus === 'fulfilled',
      error: this._error,
    }
  }

  get loading() {
    return this.status.isLoading
  }
}
